<template>
  <div class="order-list-item" :class="statusClass">
    <div class="infos">
      <div class="order-list-item__header">
        <div class="order-list-item__number">{{ $t('orderList.orderNo', { id: order.orderId || '--' }) }}</div>
        <span v-if="order.orderId" class="order-list-item__copy" role="button" @click.stop="copyOrderId"></span>
      </div>
      <div class="order-list-item__divider"></div>
      <div class="order-list-item__meta">
        <div class="order-list-item__date">
          <van-icon name="calendar-o" />
          <span>{{ order.createTime | rentalDateTimeFormat }}</span>
        </div>
        <span class="order-list-item__status" :class="statusClass">{{ statusText }}</span>
      </div>

      <div class="order-list-item__product" @click="emitDetail">
        <div class="order-list-item__image" v-lazy:background-image="order.goodsPic"></div>
        <div class="order-list-item__product-content">
          <div class="order-list-item__product-title">{{ order.goodsName || '--' }}</div>
          <div class="order-list-item__sku">{{ skuInfo }}</div>
          <span v-if="paymentBadge" class="order-list-item__payment" :class="'order-list-item__payment--' + paymentBadge.type">
            {{ $t(paymentBadge.label) }}
          </span>
        </div>
        <van-icon name="arrow" class="order-list-item__product-arrow" />
      </div>

      <div class="order-list-item__divider order-list-item__divider--section"></div>
      <div class="order-list-item__amounts">
        <div class="order-list-item__amount">
          <span>{{ $t('orderList.deposit') }}</span>
          <strong>{{ priceUnit }}{{ depositAmountText }}</strong>
        </div>
        <i></i>
        <div class="order-list-item__amount">
          <span>{{ $t('orderPage.firstRentalAmount') }}</span>
          <strong>{{ priceUnit }}{{ firstRentalAmountText }}</strong>
        </div>
      </div>

      <div
        v-if="showLogisticsCard"
        class="order-list-item__logistics"
        :class="[statusClass, { 'is-clickable': canViewLogisticsDetail }]"
        @click="onLogisticsClick"
      >
        <van-icon :name="logisticsIcon" />
        <span class="order-list-item__logistics-label">{{ logisticsLabel }}</span>
        <span v-if="logisticsTime" class="order-list-item__logistics-time">· {{ logisticsTime | rentalDateTimeFormat }}</span>
        <van-icon v-if="canViewLogisticsDetail" name="arrow" class="order-list-item__logistics-arrow" />
      </div>

      <div class="order-list-item__divider order-list-item__divider--footer"></div>
      <div class="options">
        <button type="button" @click="handlerClick('orderDetail')">{{ $t('orderList.options.orderDetail') }}</button>
        <button v-if="isPendingPayment" type="button" class="primary" @click="handlerClick('payNow')">{{ $t('orderList.options.payNow') }}</button>
        <span v-if="isPendingPayment && breachOrderStatus" class="options__break"></span>
        <button v-if="breachOrderStatus" type="button" class="options__terminal" @click="handlerClick('terminalDetail')">{{ $t('orderList.options.terminalDetail') }}</button>
      </div>
    </div>

    <Popup v-model="showCancelDialog" position="bottom" class="cancel-popup-wrap">
      <div class="cancel-popup">
        <div class="cancel-popup__close" @click="showCancelDialog = false">
          <img src="@/assets/img/close.png" alt="">
        </div>
        <div class="cancel-popup__title">{{ $t('orderList.cancelTitle') }}</div>
        <div class="cancel-popup__content">
          <div class="cancel-popup__product flex">
            <div class="cancel-popup__product-img" v-lazy:background-image="order.goodsPic"></div>
            <div class="cancel-popup__product-info flex-1 mg-l-20">
              <div class="text-ellipsis f-b">{{ order.goodsName }}</div>
              <div class="mg-t-12 cgray">{{ skuInfo }}</div>
            </div>
          </div>
          <div class="cancel-popup__amount">
            <span>Amount</span>
            <span class="cancel-popup__amount-value">{{ priceUnit }}{{ (order.firstPayAmount || 0) | numberFormat }}</span>
          </div>
          <div v-if="showCancelRefundFields" class="cancel-popup__refund">
            <div class="cancel-popup__label">{{ $t('orderList.refundType') }}</div>
            <van-radio-group v-model="refundType" direction="horizontal" class="cancel-popup__radio-group">
              <van-radio name="BANK">{{ $t('orderList.bankCard') }}</van-radio>
              <van-radio name="VA">{{ $t('orderList.virtualAccount') }}</van-radio>
              <van-radio name="E_WALLET">{{ $t('orderList.eWallet') }}</van-radio>
            </van-radio-group>
            <div v-if="refundType" class="cancel-popup__field">
              <div class="cancel-popup__label">{{ $t('orderList.bankName') }}</div>
              <div class="cancel-popup__selector" @click="openBankPicker">{{ selectedBankName || bankSelectorPlaceholder }}</div>
            </div>
            <div v-if="refundType" class="cancel-popup__field">
              <div class="cancel-popup__label">{{ $t('orderList.accountNo') }}</div>
              <van-field v-model="refundAccountNo" :placeholder="$t('orderList.accountNoPlaceholder')" maxlength="30" />
            </div>
            <div v-if="refundType" class="cancel-popup__field">
              <div class="cancel-popup__label">{{ $t('orderList.accountName') }}</div>
              <van-field v-model="refundAccountName" :placeholder="$t('orderList.accountNamePlaceholder')" maxlength="64" />
            </div>
          </div>
          <div class="cancel-popup__amount">
            <span>{{ $t('orderList.cancelReason') }}</span>
          </div>
          <div class="cancel-popup__reason">
            <van-field
              v-model="cancelReason"
              rows="5"
              autosize
              type="textarea"
              maxlength="200"
              :placeholder="$t('orderList.cancelReasonPlaceholder')"
              show-word-limit
            />
          </div>
        </div>
        <div class="cancel-popup__footer">
          <div class="btn-primary btn-normal" @click="onCancelConfirm">{{ $t('confirm') }}</div>
        </div>
      </div>
    </Popup>
    <Picker ref="bankPicker" :columns="currentBankList" :valueKey="bankValueKey" :lock-scroll="false" @confirm="onBankPickerConfirm"></Picker>
  </div>
</template>

<script>
import {BREACH_ORDER_STATUS, ORDER_STATUS, PAY_METHOD, isCodOrder} from '@/common/constants';
import { Dialog, Field, Icon, Popup, Radio, RadioGroup } from 'vant';
import {UserRefundApplyAuditStatus} from "@/api/order";
import {queryBankList} from '@/api/common';
import Picker from '@/components/picker.vue';
import { copyText } from '@/utils/clipboard';
import { formatRupiahAmount } from '@/utils/money';

const RISK_AUDIT_STATUS = {
  PASSED: 'PASSED',
  PENDING: 'PENDING',
  REVIEWING: 'REVIEWING'
};

export default {
  name: 'OrderListItem',
  components: {
    Popup,
    Picker,
    'van-dialog': Dialog.Component,
    'van-field': Field,
    'van-icon': Icon,
    'van-radio': Radio,
    'van-radio-group': RadioGroup
  },
  props: {
    order: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      showCancelDialog: false,
      cancelReason: '',
      refundType: '',
      bankListType1: [],
      bankListType2: [],
      selectedBankId: '',
      selectedBankName: '',
      refundAccountNo: '',
      refundAccountName: '',
      bankListLoaded: false,
      bankValueKey: 'bankName',
    }
  },
  computed: {
    paymentBadge() {
      switch (this.normalizeStatus(this.order.payMethod)) {
        case PAY_METHOD.ONLINE:
          return { type: 'online', label: 'orderPage.onlinePaymentTitle' };
        case PAY_METHOD.COD:
          return { type: 'cod', label: 'orderPage.codOptionTitle' };
        case PAY_METHOD.STORE:
          return { type: 'store', label: 'orderPage.storeOptionTitle' };
        default:
          return null;
      }
    },
    btnStatus() {
      const status = this.statusValue;
      const userCancelStatus = this.order.userCancelStatus;
      return {
        cancelOrder: (this.isCod ? status === ORDER_STATUS.DELIVERY : (status === ORDER_STATUS.PAYMENT || status === ORDER_STATUS.DELIVERY)) && [UserRefundApplyAuditStatus.NONE].includes(userCancelStatus),
        rentalPlan: this.isCod ? [ORDER_STATUS.RETURN, ORDER_STATUS.SUCCESS].includes(status) : [ORDER_STATUS.DELIVERY, ORDER_STATUS.LOGISTICS, ORDER_STATUS.RECEIVE, ORDER_STATUS.RETURN, ORDER_STATUS.SUCCESS, ORDER_STATUS.BREACH].includes(status),
        deliveryProgress: [ORDER_STATUS.RECEIVE, ORDER_STATUS.RETURN, ORDER_STATUS.SUCCESS, ORDER_STATUS.BREACH].includes(status),
        orderDetail: true
      }
    },
    isCod() {
      return isCodOrder(this.order);
    },
    statusValue() {
      return String(this.order.status || '').trim().toUpperCase();
    },
    breachOrderStatus() {
      return String(this.order.breachOrderStatus || '').trim().toUpperCase();
    },
    // eslint-disable-next-line vue/return-in-computed-property
    statusClass() {
      if (this.breachOrderStatus === BREACH_ORDER_STATUS.COMPLETED || this.statusValue === ORDER_STATUS.BREACH) {
        return 'status-green';
      }
      if (this.statusValue === ORDER_STATUS.RETURN && this.breachOrderStatus === BREACH_ORDER_STATUS.PROCESSING) {
        return 'status-purple';
      }
      const status = this.statusValue;
      if (status === ORDER_STATUS.PAYMENT) {
        return 'status-orange';
      } else if ([ORDER_STATUS.DELIVERY,ORDER_STATUS.LOGISTICS, ORDER_STATUS.RECEIVE, ORDER_STATUS.RETURN].includes(status)) {
        return 'status-blue';
      } else if ([ORDER_STATUS.SUCCESS, ORDER_STATUS.BREACH].includes(status)) {
        return 'status-green';
      } else if ([ORDER_STATUS.CLOSED, ORDER_STATUS.CANCELLED].includes(status)) {
        return 'status-gray';
      }
      return 'status-blue';
    },
    statusText() {
      const status = this.statusValue;
      if (status === ORDER_STATUS.BREACH || this.breachOrderStatus === BREACH_ORDER_STATUS.COMPLETED) {
        return this.$t('orderList.status.breach');
      }
      if (status === ORDER_STATUS.RETURN && this.breachOrderStatus === BREACH_ORDER_STATUS.PROCESSING) {
        return this.$t('orderPage.returningTitle');
      }
      switch (status) {
        case ORDER_STATUS.PAYMENT:
          return this.$t('orderList.status.waitingPay');
        case ORDER_STATUS.DELIVERY:
        case ORDER_STATUS.LOGISTICS:
        case ORDER_STATUS.RECEIVE:
        case ORDER_STATUS.RETURN:
          return this.$t('orderList.status.underLease');
        case ORDER_STATUS.SUCCESS:
          return this.$t('orderList.status.completed');
        case ORDER_STATUS.CLOSED:
        case ORDER_STATUS.CANCELLED:
          return this.$t('orderList.status.closed');
        default:
          return this.$t('orderList.status.underLease');
      }
    },
    priceUnit() {
      return this.$t('priceUnit');
    },
    isPendingPayment() {
      return this.statusValue === ORDER_STATUS.PAYMENT;
    },
    riskAuditStatus() {
      return this.normalizeStatus(this.order.riskAuditStatus);
    },
    isRiskAuditAwaitingApproval() {
      return this.statusValue === ORDER_STATUS.DELIVERY &&
        [RISK_AUDIT_STATUS.PENDING, RISK_AUDIT_STATUS.REVIEWING].includes(this.riskAuditStatus);
    },
    isCodApprovedAwaitingShipment() {
      return this.isCod &&
        this.riskAuditStatus === RISK_AUDIT_STATUS.PASSED &&
        [ORDER_STATUS.DELIVERY, ORDER_STATUS.LOGISTICS].includes(this.statusValue) &&
        !String(this.order.expressId || '').trim();
    },
    firstRentalAmount() {
      return [this.order.planPrice, this.order.riskAmount].reduce((total, amount) => total + Math.round((Number(amount) || 0) * 100), 0) / 100;
    },
    depositAmountText() {
      return this.formatRupiah(this.order.depositAmount);
    },
    firstRentalAmountText() {
      return this.formatRupiah(this.firstRentalAmount);
    },
    skuInfo() {
      const cycle = this.order.planUnit ? this.$t(`cycle.${this.order.planUnit}`) : '';
      return [this.order.skuInfo, this.order.planIndex, cycle].filter(Boolean).join(' · ') || '--';
    },
    showLogisticsCard() {
      if (this.isRiskAuditAwaitingApproval) return false;
      if (this.normalizeStatus(this.order.payMethod) === 'STORE' && !String(this.order.expressId || '').trim()) return false;
      if (
        this.isCod &&
        Number(this.order.codShippingFee || 0) > 0 &&
        this.normalizeStatus(this.order.codShippingFeePayStatus) === 'PENDING_PAYMENT'
      ) {
        return false;
      }
      return !this.breachOrderStatus && [ORDER_STATUS.DELIVERY, ORDER_STATUS.LOGISTICS, ORDER_STATUS.RECEIVE, ORDER_STATUS.RETURN].includes(this.statusValue);
    },
    canViewLogisticsDetail() {
      return this.statusValue === ORDER_STATUS.RECEIVE && this.order.canViewLogisticsDetail && !!this.order.expressId;
    },
    logisticsIcon() {
      if (this.isRiskAuditAwaitingApproval || this.isCodApprovedAwaitingShipment) return 'bag-o';
      const status = this.statusValue;
      if (status === ORDER_STATUS.DELIVERY) return 'bag-o';
      if (status === ORDER_STATUS.LOGISTICS) return 'logistics';
      if (status === ORDER_STATUS.RECEIVE) return 'logistics';
      if (status === ORDER_STATUS.RETURN) return 'passed';
      return 'logistics';
    },
    logisticsLabel() {
      if (this.isRiskAuditAwaitingApproval) return this.$t('orderPage.awaitingApprovalDesc');
      if (this.isCodApprovedAwaitingShipment) return this.$t('orderPage.statusAwaitingShipment');
      const status = this.statusValue;
      if ([ORDER_STATUS.DELIVERY, ORDER_STATUS.LOGISTICS].includes(status)) return this.$t('orderPage.statusAwaitingShipment');
      if (status === ORDER_STATUS.RECEIVE) return this.$t('orderPage.statusAwaitingDelivery');
      if (status === ORDER_STATUS.RETURN) return this.$t('orderList.logistics.signed');
      return '';
    },
    logisticsTime() {
      if (this.isRiskAuditAwaitingApproval) return '';
      return this.statusValue === ORDER_STATUS.RETURN ? this.order.receiveTime : this.order.updateTime;
    },
    isDelivery() {
      return this.statusValue === ORDER_STATUS.DELIVERY;
    },
    isBackTrackRefund() {
      return Boolean(this.order?.canBackTrack);
    },
    isDeliveryNonCodOrder() {
      return this.isDelivery && !this.isCod;
    },
    showCancelRefundFields() {
      return this.isDeliveryNonCodOrder && !this.isBackTrackRefund;
    },
    showCodReminder() {
      return this.isCod && [ORDER_STATUS.DELIVERY, ORDER_STATUS.RECEIVE].includes(this.statusValue);
    },
    codReminderHtml() {
      const amount = formatRupiahAmount(this.order.firstPayAmount || 0);
      const text = this.$t('orderPage.codReminder', { amount });
      return text.replace(
        'Rp' + amount,
        '<span style="color:#FF7A00;font-weight:600">Rp' + amount + '</span>'
      );
    },
    currentBankList() {
      if (this.refundType === 'E_WALLET') {
        return this.bankListType2;
      }
      return this.bankListType1;
    },
    bankSelectorPlaceholder() {
      switch (this.refundType) {
        case 'BANK': return this.$t('orderList.selectBankPlaceholder')
        case 'VA': return this.$t('orderList.selectVAPlaceholder')
        case 'E_WALLET': return this.$t('orderList.selectEWalletPlaceholder')
        default: return ''
      }
    }
  },
  watch: {
    refundType() {
      this.selectedBankId = ''
      this.selectedBankName = ''
      this.refundAccountNo = ''
      this.refundAccountName = ''
    }
  },
  methods: {
    normalizeStatus(value = '') {
      return String(value == null ? '' : value).trim().toUpperCase();
    },
    formatRupiah(amount) {
      return formatRupiahAmount(amount) || '0';
    },
    async copyOrderId() {
      if (!this.order.orderId) return;
      const success = await copyText(this.order.orderId);
      this.$toast(this.$t(success ? 'orderList.copySuccess' : 'orderList.copyFailed'));
    },
    emitDetail() {
      this.$emit('orderClick', { type: 'orderDetail', item: this.order });
    },
    handlerClick(type) {
      if (type === 'cancelOrder' && this.isDelivery) {
        this.cancelReason = ''
        this.refundType = ''
        this.selectedBankId = ''
        this.selectedBankName = ''
        this.refundAccountNo = ''
        this.refundAccountName = ''
        this.loadBankList()
        this.showCancelDialog = true
        return
      }
      this.$emit('orderClick', {
        type: type,
        item: this.order,
      })
    },
    onCancelConfirm() {
      this.showCancelDialog = false
      const clickData = {
        type: 'cancelOrder',
        item: this.order,
        reason: this.cancelReason,
      }
      if (this.showCancelRefundFields) {
        if (!this.refundType) {
          this.$toast(this.$t('orderList.refundTypeEmpty'))
          this.showCancelDialog = true
          return
        }
        if (!this.selectedBankId) {
          this.$toast(this.$t('orderList.bankEmpty'))
          this.showCancelDialog = true
          return
        }
        if (!this.refundAccountNo) {
          this.$toast(this.$t('orderList.accountNoEmpty'))
          this.showCancelDialog = true
          return
        }
        if (!this.refundAccountName) {
          this.$toast(this.$t('orderList.accountNameEmpty'))
          this.showCancelDialog = true
          return
        }
        if (!this.validateAccountNo(this.refundAccountNo, this.refundType)) {
          this.$toast(this.$t('orderList.accountNoError'))
          this.showCancelDialog = true
          return
        }
        if (!/^[a-zA-Z0-9,\.'\-\s]+$/.test(this.refundAccountName)) {
          this.$toast(this.$t('orderList.accountNameError'))
          this.showCancelDialog = true
          return
        }
        Object.assign(clickData, {
          refundType: this.refundType,
          refundBankId: this.selectedBankId,
          refundBankName: this.selectedBankName,
          accountNo: this.refundAccountNo,
          accountName: this.refundAccountName,
        })
      }
      this.$emit('orderClick', clickData)
    },
    detectDataKeys(data) {
      const item = data && data[0]
      if (!item) return
      const nameKeys = ['bank_name', 'bankName', 'name', 'label', 'text']
      for (const key of nameKeys) {
        if (item[key] !== undefined) {
          this.bankValueKey = key
          break
        }
      }
    },
    validateAccountNo(accountNo, type) {
      if (type === 'VA') return /^\d{12,28}$/.test(accountNo)
      if (type === 'BANK') return /^\d{1,16}$/.test(accountNo)
      if (!/^\d{8,19}$/.test(accountNo)) return false
      return /^(8|08|628|6208)/.test(accountNo)
    },
    loadBankList() {
      Promise.all([
        queryBankList({ type: 1 }),
        queryBankList({ type: 2 })
      ]).then(([res1, res2]) => {
        if (res1?.code === 'Success') {
          this.bankListType1 = res1.data || []
          if (this.bankListType1.length) this.detectDataKeys(this.bankListType1)
        }
        if (res2?.code === 'Success') {
          this.bankListType2 = res2.data || []
          if (this.bankListType2.length && !this.bankValueKey) this.detectDataKeys(this.bankListType2)
        }
        this.bankListLoaded = true
      }).catch(e => {
        console.error('Bank list load failed', e)
      })
    },
    openBankPicker() {
      this.$refs.bankPicker.open()
    },
    onBankPickerConfirm(value) {
      const item = Array.isArray(value) ? value[0] : value
      if (!item) return
      const idKeys = ['bank_id', 'id', 'bankId', 'code']
      for (const key of idKeys) {
        if (item[key] !== undefined) {
          this.selectedBankId = item[key]
          break
        }
      }
      this.selectedBankName = item[this.bankValueKey] || ''
    },
    onLogisticsClick() {
      if (this.canViewLogisticsDetail) {
        this.$emit('orderClick', { type: 'deliveryProgress', item: this.order })
      }
    }
  }
}
</script>

<style lang="less" scoped>
.order-list-item {
  margin-top: 30px;
  position: relative;
  width: 100%;

  &:first-child {
    margin-top: 0;
  }
}

.order-list-item .infos { border-left: 6px solid #2547f1; }
.order-list-item.status-orange .infos { border-left-color: #fc5d18; }
.order-list-item.status-purple .infos { border-left-color: #5128c7; }
.order-list-item.status-green .infos { border-left-color: #68aa36; }
.order-list-item.status-gray .infos { border-left-color: #68686c; }

.order-list-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  border-radius: 12px 0 0 12px;
  background: #2547f1;
}
.order-list-item.status-purple::before { background: #5128c7; }
.order-list-item.status-green::before { background: #68aa36; }
.order-list-item.status-gray::before { background: #68686c; }

.order-header-row { display: flex; align-items: center; }
.order-no { color: #1f2a5a; font-size: 28px; font-weight: 700; flex: 1; padding-bottom: 12px; border-bottom: 1px solid #e5e7eb; }
.order-no .van-icon { margin-left: 8px; color: #6b7280; font-size: 28px; }
.order-meta-row { display: flex; justify-content: space-between; align-items: center; margin-top: 16px; }
.detail-arrow { margin-left: auto; color: #9ca3af; font-size: 34px; }
.amount-panel { display: flex; align-items: center; margin-top: 26px; padding: 22px 0; border-top: 1px solid #e5e7eb; border-bottom: 1px solid #e5e7eb; }
.amount-panel > div { flex: 1; text-align: center; }
.amount-panel span { display: block; color: #6b7280; font-size: 24px; }
.amount-panel strong { display: block; margin-top: 8px; color: #2547f1; font-size: 30px; font-weight: 700; }
.amount-panel i { width: 1px; height: 60px; background: #e5e7eb; }

.cgray {
  color: var(--text-color-gray);
}

.status-common() {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  line-height: 1;
  padding: 3px 12px;
  border-radius: 8px;
}

.status {
  &-waiting {
    .status-common();
    color: #FF5A00;
    background: rgba(255, 90, 0, 0.14);
  }

  &-orange {
    .status-common();
    color: #fc5d18;
    background: rgb(252, 234, 224, 0.16);
  }

  &-active {
    .status-common();
    color: #2547F1;
    background: rgba(36, 71, 241, 0.14);
  }

  &-blue {
    .status-common();
    color: #325af0;
    background: rgb(229, 236, 252, 0.16);
  }

  &-completed {
    .status-common();
    color: #68AA36;
    background: rgba(104, 170, 54, 0.16);
  }

  &-green {
    .status-common();
    color: #7eb256;
    background: rgb(229, 239, 220, 0.16);
  }

  &-cancelled {
    .status-common();
    color: #999999;
    background: rgba(153, 153, 153, 0.16);
  }

  &-gray {
    .status-common();
    color: #68686c;
    background: rgb(239, 239, 239, 0.16);
  }

  &-cyan {
    .status-common();
    color: #2aa7a5;
    background: rgb(224, 244, 245, 0.16);
  }

  &-purple {
    .status-common();
    color: #5128c7;
    background: rgb(232, 229, 251, 0.16);
  }
}

.t1 {
  font-size: 26px;
}

.order-list-item__badges {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.cod-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 12px;
  border-radius: 8px;
  background: rgba(255, 122, 0, 0.12);
  color: #FF7A00;
  font-size: 24px;
  line-height: 1;
  font-weight: 600;
}

.amount-due {
  color: #E53935;
  font-weight: 600;
}

.cod-reminder {
  margin-top: 20px;
  padding: 18px 20px;
  border-radius: 8px;
  background: #FFF4EC;
  color: #B45309;
  font-size: 24px;
  line-height: 34px;
}

.infos {
  box-sizing: border-box;
  width: 100%;
  padding: 30px;
  background: #FFFFFF;
  box-shadow: 0px 8px 30px 0px rgba(0, 0, 0, 0.08);
  border-radius: var(--border-radius-base);

  .info-img {
    width: 160px;
    height: 160px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 8px;
    overflow: hidden;
  }

  .info-item {
    margin-left: 20px;
    font-size: 26px;
    line-height: 26px;
    color: var(--text-color-gray);
    overflow: hidden;

    >div:first-child {
      font-weight: 500;
      color: var(--text-color-black);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
}

.options {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  >div {
    //margin-left: 12px;
    margin-top: 16px;
    background: linear-gradient(180deg, #FFFFFF 0%, #FFFFFF 100%);
    border-radius: 22px;
    border: 2px solid #2547F1;
    font-size: 26px;
    color: #2547F1;
    line-height: 38px;
    text-align: center;
    padding: 0 20px;
    font-weight: 500;
    min-width: 0;
    flex: 0 0 auto;
    font-size: 22px;
    padding-left: 14px;
    padding-right: 14px;
  }
  .primary { color: #fff; background: #2547f1; }
}

.order-list-item__badges > span[class*="status-"]::before {
  content: '';
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
  background: currentColor;
}

.logistics-card {
  margin-top: 20px;
  padding: 24px;
  background: #F0F4FF;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;

  &__left {
    flex: 1;
    overflow: hidden;
  }

  &__header {
    display: flex;
    align-items: center;
    font-size: 24px;
    color: var(--primary-color);
  }

  &__icon {
    margin-right: 8px;
    font-size: 38px;
  }

  &__label {
    margin-right: 8px;
  }

  &__status {
    font-weight: 600;
  }

  &__info {
    margin-top: 10px;
    font-size: 24px;
    line-height: 34px;
    color: #6B7280;
  }

  &__arrow {
    font-size: 26px;
    color: var(--primary-color);
    flex-shrink: 0;
    margin-left: 12px;
  }
}

/* Android My Orders card alignment. Design values use the existing 750px H5 scale. */
.order-list-item {
  margin-top: 16px;
}

.order-list-item.status-orange,
.order-list-item.status-blue,
.order-list-item.status-purple,
.order-list-item.status-green,
.order-list-item.status-gray {
  display: block;
  padding: 0;
  color: inherit;
  line-height: normal;
  background: transparent;
  border-radius: 0;
}

.order-list-item::before {
  display: none;
}

.order-list-item .infos {
  width: 100%;
  padding: 16px;
  overflow: hidden;
  background: #fff;
  border: 1px solid #e5eaf3;
  border-left: 6px solid #2563eb;
  border-radius: 20px;
  box-shadow: none;
}

.order-list-item.status-orange .infos { border-left-color: #ff8a00; }
.order-list-item.status-purple .infos { border-left-color: #7c3aed; }
.order-list-item.status-green .infos { border-left-color: #22c55e; }
.order-list-item.status-gray .infos { border-left-color: #64748b; }

.order-list-item__header,
.order-list-item__meta,
.order-list-item__product,
.order-list-item__amounts,
.order-list-item__logistics,
.options {
  display: flex;
  align-items: center;
}

.order-list-item__header {
  min-width: 0;
}

.order-list-item__number {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  color: #1f2a5a;
  font-size: 26px;
  font-weight: 700;
  line-height: 36px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-list-item__copy {
  position: relative;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  margin-left: 12px;
}

.order-list-item__copy::before,
.order-list-item__copy::after {
  position: absolute;
  width: 20px;
  height: 22px;
  border: 2px solid #2563eb;
  border-radius: 2px;
  content: '';
}

.order-list-item__copy::before {
  top: 5px;
  left: 5px;
  opacity: .7;
}

.order-list-item__copy::after {
  top: 1px;
  left: 9px;
  background: #fff;
}

.order-list-item__divider {
  height: 1px;
  background: #e5e7eb;
}

.order-list-item__header + .order-list-item__divider {
  margin: 12px 0;
}

.order-list-item__meta {
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
}

.order-list-item__date {
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;
  color: #6b7280;
  font-size: 24px;
  font-weight: 400;
  line-height: 32px;
}

.order-list-item__date .van-icon {
  flex: 0 0 auto;
  margin-right: 8px;
  font-size: 28px;
}

.order-list-item__date span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-list-item__status {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  max-width: 48%;
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 22px;
  font-weight: 500;
  line-height: 28px;
  white-space: nowrap;
}

.order-list-item__status::before {
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
  background: currentColor;
  content: '';
}

.order-list-item__status.status-orange { color: #ff8a00; background: #fff3e3; }
.order-list-item__status.status-blue { color: #2563eb; background: #eff5ff; }
.order-list-item__status.status-purple { color: #7c3aed; background: #f3efff; }
.order-list-item__status.status-green { color: #22c55e; background: #eafbf2; }
.order-list-item__status.status-gray { color: #64748b; background: #f3f4f6; }

.order-list-item__product {
  min-width: 0;
  margin-top: 16px;
  padding: 0;
  background: #fff;
  border-radius: 0;
}

.order-list-item__image {
  flex: 0 0 144px;
  width: 144px;
  height: 144px;
  overflow: hidden;
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  border-radius: 8px;
}

.order-list-item__product-content {
  flex: 1;
  min-width: 0;
  margin-left: 16px;
}

.order-list-item__product-title {
  overflow: hidden;
  color: #1f2a5a;
  font-size: 26px;
  font-weight: 700;
  line-height: 36px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-list-item__sku {
  margin-top: 8px;
  overflow: hidden;
  color: #6b7280;
  font-size: 24px;
  font-weight: 400;
  line-height: 32px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-list-item__product-arrow {
  flex: 0 0 auto;
  margin-left: 8px;
  color: #9ca3af;
  font-size: 32px;
}

.order-list-item__payment {
  display: inline-block;
  box-sizing: border-box;
  max-width: 100%;
  margin-top: 8px;
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 22px;
  line-height: 28px;
  overflow-wrap: anywhere;
}

.order-list-item__payment--online { color: #2563eb; background: #eff5ff; }
.order-list-item__payment--cod { color: #ff8a00; background: #fff3e3; }
.order-list-item__payment--store { color: #7c3aed; background: #f3efff; }

.order-list-item__divider--section,
.order-list-item__divider--footer {
  margin: 16px 0;
}

.order-list-item__amounts {
  justify-content: space-between;
}

.order-list-item__amount {
  flex: 1;
  min-width: 0;
  text-align: center;
}

.order-list-item__amount span,
.order-list-item__amount strong {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-list-item__amount span {
  color: #6b7280;
  font-size: 24px;
  line-height: 32px;
}

.order-list-item__amount strong {
  margin-top: 8px;
  color: #2563eb;
  font-size: 30px;
  font-weight: 700;
  line-height: 40px;
}

.order-list-item__amounts > i {
  flex: 0 0 1px;
  width: 1px;
  height: 76px;
  background: #e5e7eb;
}

.order-list-item__logistics {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-width: 0;
  min-height: 64px;
  margin-top: 16px;
  padding: 12px 16px;
  border: 1px solid currentColor;
  border-radius: 12px;
  font-size: 24px;
  font-weight: 600;
  line-height: 32px;
  text-align: left;
}

.order-list-item__logistics > .van-icon:first-child {
  flex: 0 0 auto;
  margin-right: 12px;
  font-size: 30px;
}

.order-list-item__logistics-label {
  flex: 1;
  min-width: 0;
}

.order-list-item__logistics-time {
  min-width: 0;
  overflow: hidden;
  font-weight: 400;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.order-list-item__logistics-arrow {
  flex: 0 0 auto;
  margin-left: auto;
  font-size: 28px;
}

.order-list-item__logistics.is-clickable { cursor: pointer; }
.order-list-item__logistics.status-orange { color: #ff8a00; background: #fff3e3; border-color: rgba(255, 138, 0, .24); }
.order-list-item__logistics.status-blue { color: #2563eb; background: #eff5ff; border-color: rgba(37, 99, 235, .24); }
.order-list-item__logistics.status-purple { color: #7c3aed; background: #f3efff; border-color: rgba(124, 58, 237, .24); }
.order-list-item__logistics.status-green { color: #22c55e; background: #eafbf2; border-color: rgba(34, 197, 94, .24); }
.order-list-item__logistics.status-gray { color: #64748b; background: #f3f4f6; border-color: rgba(100, 116, 139, .24); }

.options {
  justify-content: flex-end;
  gap: 20px;
  flex-wrap: wrap;
}

.options button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 228px;
  height: 80px;
  padding: 0;
  color: #2563eb;
  font-size: 24px;
  font-weight: 500;
  line-height: 32px;
  background: #fff;
  border: 2px solid #2563eb;
  border-radius: 16px;
}

.options button.primary {
  color: #fff;
  background: #2563eb;
}

.options button.options__terminal {
  width: 260px;
}

.options__break {
  flex-basis: 100%;
  height: 0;
}

::v-deep .van-popup.cancel-popup-wrap {
  background: none;
}

.cancel-popup {
  width: 750px;
  background: #FFFFFF;
  border-radius: 50px 50px 0px 0px;
  filter: blur(0px);
  position: relative;
  display: flex;
  flex-direction: column;
  max-height: 85vh;

  &__close {
    position: absolute;
    right: 22px;
    top: 28px;
    width: 40px;
    height: 40px;
    z-index: 1;

    img {
      margin: 8px auto;
      display: block;
      width: 24px;
      height: 24px;
    }
  }

  &__title {
    font-size: 42px;
    line-height: 48px;
    font-weight: 600;
    text-align: center;
    padding: 30px 30px 20px;
  }

  &__content {
    flex: 1;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 0 30px;
  }

  &__product {
    padding: 20px;
    background: #F8FAFE;
    border-radius: 12px;
  }

  &__product-img {
    width: 120px;
    height: 120px;
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 8px;
    flex-shrink: 0;
  }

  &__product-info {
    font-size: 26px;
    line-height: 36px;
    color: var(--text-color-black);
  }

  &__amount {
    margin-top: 24px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 28px;
    color: var(--text-color-black);

    &-value {
      color: var(--red-color);
      font-weight: 500;
    }
  }

  &__reason {
    margin-top: 24px;
    border: 1px solid var(--border-color-base);
    border-radius: 12px;
    overflow: hidden;

    ::v-deep .van-field__control {
      font-size: 36px;
      padding: 10px 0;
      line-height: 48px;
    }
    ::v-deep .van-field__word-limit {
      font-size: 24px;
    }
  }

  &__refund {
    margin-top: 24px;
    padding: 20px;
    background: #F8FAFE;
    border-radius: 12px;

    ::v-deep .van-field__control {
      font-size: 36px;
      padding: 10px 0;
      line-height: 48px;
    }
  }

  &__radio-group {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    padding-top: 8px;

    ::v-deep .van-radio {
      margin-bottom: 8px;
    }

    ::v-deep .van-radio__label {
      font-size: 28px;
    }

    ::v-deep .van-radio__icon {
      font-size: 32px;
    }
  }

  &__label {
    font-size: 28px;
    font-weight: 500;
    color: var(--text-color-black);
    margin-bottom: 12px;
  }

  &__field {
    margin-top: 20px;
  }

  &__selector {
    border: 1px solid var(--border-color-base);
    border-radius: var(--border-radius-base);
    padding: 20px;
    font-size: 28px;
    color: var(--text-color-gray);
    position: relative;
    background: #fff;

    &::after {
      position: absolute;
      content: '';
      width: 24px;
      height: 24px;
      right: 20px;
      top: 50%;
      background: #ffffff url(~@/assets/img/next.png) no-repeat right center/auto 24px;
      transform: translateY(-50%) rotate(90deg);
    }
  }

  &__footer {
    padding: 30px;
    background: #FFFFFF;
  }
}
</style>
